/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {getPayload} from 'payload';

import {SeoHead} from 'UI/components/layout/SeoHead';

import payloadConfig from '@payload-config';
import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import {AppLayout} from 'UI/layouts/AppLayout';
import {FaqBlock} from 'UI/modules/blocks/FaqBlock';

import {getLegalPages} from 'Payload/utils/getLegalPages';
import {buildFaqJsonLd} from 'UI/utils/jsonLd';

import type {GetStaticProps} from 'next';
import type {Payload} from 'payload';
import type {NextSSGPageWithLayout} from 'types/global';
import type {Config, LegalPage} from 'types/payload-types';

interface FaqPageData {
    id: string;
    items?: {answer: string; id?: string; question: string}[];
    meta?: {description?: string | null; title?: string | null};
    title: string;
}

const REVALIDATE_SECONDS = 18000;

interface FaqProps {
    faqPage: FaqPageData | null;
    legalPages: LegalPage[];
}

/**
 * FAQ-Seite mit häufig gestellten Fragen und Antworten.
 *
 * @param props         Die Seiten-Props.
 * @param props.faqPage Das FAQ-Global-Dokument aus dem CMS.
 * @returns ReactNode der FAQ-Seite.
 */
const Faq: NextSSGPageWithLayout<typeof getStaticProps> = ({faqPage}) => {
    if (!faqPage) return null;

    return (
        <Content data-cy="FaqPage">
            <SeoHead
                description={faqPage.meta?.description}
                jsonLd={faqPage.items && faqPage.items.length > 0 ? [
                    buildFaqJsonLd(faqPage.items, [
                        {
                            name: 'Home',
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/`
                        },
                        {
                            name: faqPage.title,
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/faq`
                        }
                    ])
                ] : undefined}
                title={faqPage.meta?.title ?? faqPage.title}
                url={`${process.env.NEXT_PUBLIC_BASE_URL}/faq`}
            />
            <FaqBlock items={faqPage.items ?? []} title={faqPage.title} />
        </Content>
    );
};

Faq.displayName = 'Faq';

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout (Header + Navigation).
 *
 * @param _route        Die aktuelle Route.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
Faq.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="faq" legalPages={pageProps.legalPages}>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
Faq.getLayoutKey = () => 'faq';

export default Faq;

// ── Styled ──────────────────────────────────────────────────

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(8)};
    padding: ${spacing(8)} ${spacing(6)};
`;

// ── Data Fetching ───────────────────────────────────────────

/**
 * Lädt das FAQ-Global aus dem CMS.
 *
 * @param props                  Die Static-Props Kontext-Optionen.
 * @param props.locale           Die aktuelle Locale.
 * @param props.revalidateReason Revalidate Reason.
 * @returns Props mit FAQ-Seiten-Daten.
 */
export const getStaticProps: GetStaticProps<FaqProps> = async ({locale, revalidateReason}) => {
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {
            props: {
                faqPage: null,
                legalPages: []
            },
            revalidate: 1
        };
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];

    const [faqPage, legalPages] = await Promise.all([
        payload.findGlobal({
            depth: 0,
            locale: payloadLocale,
            slug: 'faq-page'
        }),
        getLegalPages(payload, payloadLocale)
    ]);

    return {
        props: {
            faqPage: JSON.parse(JSON.stringify(faqPage)) as FaqPageData,
            legalPages
        },
        revalidate: REVALIDATE_SECONDS
    };
};